import { useEffect, useState } from "react";
import { Mail, Phone, Accessibility, CheckCircle2, Heart } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

export default function Accessibilite() {
    const [referent, setReferent] = useState(null);

    useEffect(() => {
        api.get("/settings/referent-handicap")
            .then((r) => setReferent(r.data))
            .catch(() => {});
    }, []);

    return (
        <div className="bg-white">
            <SiteNav />
            <section className="border-b border-brand-line bg-brand-coal px-6 pt-32 pb-20 text-white">
                <div className="mx-auto max-w-6xl">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-orange">
                        Engagement Cursus Physio
                    </div>
                    <h1 className="mt-4 font-display text-6xl font-normal leading-[1.05] tracking-tightest">
                        Accessibilité & <span className="text-brand-orange">handicap</span>.
                    </h1>
                    <p className="mt-6 max-w-3xl text-lg text-white/70">
                        Une formation continue de qualité doit être <strong className="text-white">accessible à tous</strong>.
                        Cursus Physio s'engage à étudier toute demande d'adaptation et à proposer des solutions concrètes.
                    </p>
                </div>
            </section>

            <section className="px-6 py-20">
                <div className="mx-auto max-w-6xl">
                    {referent ? (
                        <div className="grid items-start gap-12 rounded-3xl border border-brand-line bg-white p-8 shadow-premium md:grid-cols-3" data-testid="referent-handicap-card">
                            <div className="md:col-span-1">
                                {referent.photo_url && (
                                    <img
                                        src={referent.photo_url}
                                        alt={referent.name}
                                        className="aspect-square w-full rounded-2xl object-cover"
                                    />
                                )}
                                <div className="mt-5">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                                        {referent.role || "Référente handicap"}
                                    </div>
                                    <h3 className="mt-2 font-display text-2xl text-brand-ink" data-testid="referent-name">{referent.name}</h3>
                                    <div className="mt-4 space-y-2 text-sm text-brand-ink/80">
                                        <a href={`mailto:${referent.email}`} className="flex items-center gap-2 hover:text-brand-orange" data-testid="referent-email">
                                            <Mail className="h-3.5 w-3.5 text-brand-orange" /> {referent.email}
                                        </a>
                                        {referent.phone && (
                                            <a href={`tel:${referent.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-brand-orange">
                                                <Phone className="h-3.5 w-3.5 text-brand-orange" /> {referent.phone}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Notre engagement</div>
                                <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-brand-ink/85">
                                    {referent.message}
                                </p>
                                {referent.adaptations?.length > 0 && (
                                    <>
                                        <div className="mt-8 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                                            Adaptations possibles
                                        </div>
                                        <ul className="mt-4 grid gap-3 md:grid-cols-2">
                                            {referent.adaptations.map((a, i) => (
                                                <li key={i} className="flex items-start gap-2 rounded-xl bg-brand-sand px-4 py-3 text-sm text-brand-ink/85">
                                                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                                    {a}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-brand-smoke">Chargement…</div>
                    )}

                    <div className="mt-16 rounded-3xl border border-brand-line bg-brand-sand p-10">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-orange/15 text-brand-orange">
                                <Accessibility className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="font-display text-2xl">Comment signaler un besoin spécifique ?</h3>
                                <ol className="mt-5 space-y-3 text-brand-ink/85">
                                    <li className="border-l-2 border-brand-orange/40 pl-5">
                                        <strong>1. Prenez contact dès l'inscription</strong> avec notre référente handicap par email ou téléphone.
                                    </li>
                                    <li className="border-l-2 border-brand-orange/40 pl-5">
                                        <strong>2. Précisez votre situation</strong> : nature du handicap, besoins identifiés, formation visée. Confidentiel.
                                    </li>
                                    <li className="border-l-2 border-brand-orange/40 pl-5">
                                        <strong>3. Étude personnalisée</strong> sous 5 jours ouvrés : nous vous proposons les adaptations possibles ou orientons vers un partenaire spécialisé.
                                    </li>
                                    <li className="border-l-2 border-brand-orange/40 pl-5">
                                        <strong>4. Mise en œuvre</strong> avant le jour de la session : matériel, salle, accompagnement, supports.
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 flex items-start gap-3 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-6 text-sm">
                        <Heart className="mt-1 h-5 w-5 flex-shrink-0 text-brand-orange" />
                        <p className="text-brand-ink/85">
                            <strong>Aucune demande n'est triviale.</strong> Si vous hésitez à nous contacter par crainte d'imposer,
                            sachez que notre engagement Qualiopi nous lie : étudier toute demande, sans jugement, est une obligation
                            que nous prenons très à cœur.
                        </p>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
